import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "../lib/i18n";
import { api } from "../lib/api";
import { SectionHead, Reveal } from "./Reveal";

const FIELD = { el: { name: "Ονοματεπώνυμο", phone: "Τηλέφωνο", email: "Email", service: "Υπηρεσία", area: "Περιοχή", message: "Περιγραφή" }, en: { name: "Name", phone: "Phone", email: "Email", service: "Service", area: "Area", message: "Description" } };

const quoteError = (e, lang) => {
  const d = e?.response?.data?.detail;
  const el = lang === "el";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const fields = [...new Set(d.map((x) => FIELD[lang][x?.loc?.[x.loc.length - 1]]).filter(Boolean))];
    return (el ? "Ελέγξτε τα πεδία: " : "Please check: ") + (fields.join(", ") || "—");
  }
  if (!e?.response) return el ? "Δεν υπάρχει σύνδεση. Ανανεώστε τη σελίδα και δοκιμάστε ξανά — τα στοιχεία σας έμειναν στη φόρμα." : "No connection. Refresh and try again — your details are kept.";
  return el ? `Σφάλμα διακομιστή (${e.response.status}). Δοκιμάστε ξανά ή καλέστε στο +30 698 765 4456.` : `Server error (${e.response.status}). Try again or call +30 698 765 4456.`;
};

const empty = { name: "", phone: "", email: "", service: "renovation", area: "", message: "", website: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300">{label}</span>
    {children}
  </label>
);

export const QuoteForm = () => {
  const { t, lang } = useLang();
  const q = t.quote;
  const [f, setF] = useState(empty);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent || f.name.trim().length < 2 || f.phone.trim().length < 6 || f.area.trim().length < 2 || f.message.trim().length < 5) {
      toast.error(q.required);
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", { ...f, email: f.email.trim() || null, lang });
      toast.success(q.success);
      setDone(true);
      setF(empty);
      setConsent(false);
    } catch (err) {
      toast.error(quoteError(err, lang));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="relative overflow-hidden border-t border-emerald-500/10 bg-[#070A09] py-24 sm:py-36" data-testid="quote-section">
      <div className="quote-glow" />
      <div className="relative mx-auto grid max-w-[1400px] gap-12 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHead eyebrow={q.eyebrow} title={q.title} accent={q.accent} testid="quote-title" />
          <Reveal><p className="-mt-6 max-w-md text-base leading-relaxed text-zinc-300">{q.sub}</p></Reveal>
        </div>
        <Reveal className="lg:col-span-7" delay={0.1}>
          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="glass flex min-h-[420px] flex-col items-start justify-center rounded-[26px] p-8 sm:p-12" data-testid="quote-success">
              <CheckCircle2 className="h-14 w-14 text-[#00E676]" />
              <h3 className="mt-6 font-display text-4xl font-black uppercase text-white">{q.done}</h3>
              <p className="mt-3 text-zinc-300">{q.doneSub}</p>
              <button onClick={() => setDone(false)} className="btn-ghost mt-8" data-testid="quote-again-btn">{q.again}</button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="glass grid gap-5 rounded-[26px] p-6 sm:grid-cols-2 sm:p-10" data-testid="quote-form" noValidate>
              <Field label={`${q.name} *`}><input className="input" value={f.name} onChange={set("name")} autoComplete="name" data-testid="quote-name-input" /></Field>
              <Field label={`${q.phone} *`}><input className="input" type="tel" inputMode="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" data-testid="quote-phone-input" /></Field>
              <Field label={q.email}><input className="input" type="email" inputMode="email" value={f.email} onChange={set("email")} autoComplete="email" data-testid="quote-email-input" /></Field>
              <Field label={`${q.service} *`}>
                <select className="input appearance-none" value={f.service} onChange={set("service")} data-testid="quote-service-select">
                  {t.services.items.map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
                  <option value="other">{q.other}</option>
                </select>
              </Field>
              <div className="sm:col-span-2"><Field label={`${q.area} *`}><input className="input" value={f.area} onChange={set("area")} data-testid="quote-area-input" /></Field></div>
              <div className="sm:col-span-2"><Field label={`${q.message} *`}><textarea rows={5} className="input resize-none" placeholder={q.placeholderMsg} value={f.message} onChange={set("message")} data-testid="quote-message-input" /></Field></div>
              <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
              <label className="flex cursor-pointer items-start gap-3 text-sm text-zinc-300 sm:col-span-2">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-5 w-5 accent-[#00E676]" data-testid="quote-consent-checkbox" />
                {q.consent}
              </label>
              <button type="submit" disabled={loading} className="btn-primary btn-lg justify-center sm:col-span-2" data-testid="quote-submit-btn">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" />{q.sending}</> : <>{q.submit}<ArrowUpRight className="h-5 w-5" /></>}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
